import React from "react";
import { useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { ArrowLeft } from "lucide-react";

function LegalShell({ title, children }) {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={() => navigate("/")}><Logo /></button>
          <button onClick={() => navigate("/")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900">
            <ArrowLeft className="h-4 w-4" /> Home
          </button>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 py-12">
        <h1 className="text-3xl font-semibold text-slate-900 mb-2">{title}</h1>
        <p className="text-sm text-slate-400 mb-8">Last updated: June 2026</p>
        <div className="prose prose-slate max-w-none space-y-6 text-slate-600 leading-relaxed">{children}</div>
      </main>
      <footer className="border-t border-slate-100 py-6 text-center text-xs text-slate-400">© 2026 Syllabify</footer>
    </div>
  );
}

const H = ({ children }) => <h2 className="text-lg font-semibold text-slate-900 mt-8">{children}</h2>;

export function Privacy() {
  return (
    <LegalShell title="Privacy Policy">
      <p>Syllabify helps students turn a course syllabus into an organized semester planner. This policy explains what we collect and how your documents are handled.</p>
      <H>What we collect</H>
      <p>When you sign in with Google, we store your name, email address, and profile picture. We store the courses, events, grades, and tasks you create in Syllabify.</p>
      <H>How uploaded documents are processed</H>
      <p>When you upload a syllabus, we extract the text from the file and send that text to an AI provider (Anthropic Claude) to identify assignments, dates, and grading weights. We do not permanently store the uploaded file itself — only the structured events you choose to save to your planner.</p>
      <H>How long documents are stored</H>
      <p>The original uploaded file is processed in memory and is not retained after extraction. The structured schedule you save remains in your account until you delete it.</p>
      <H>Is your syllabus used to train AI?</H>
      <p>No. Your syllabus text is used only to generate your schedule. We do not use your uploaded documents to train AI models.</p>
      <H>Deleting your data</H>
      <p>You can delete any course or event at any time. You can delete your entire account and all associated data from Settings → Danger zone, or export your data as JSON.</p>
      <H>Security</H>
      <p>We take reasonable measures to protect your data, including server-side authorization so you can only access your own courses and events. No system can be guaranteed to be completely secure.</p>
      <H>Payments</H>
      <p>Payments are processed by Stripe. We do not store your card details. We store only a record that your account has lifetime access.</p>
      <H>Contact</H>
      <p>Questions about privacy? Contact support at support@syllabify.app.</p>
    </LegalShell>
  );
}

export function Terms() {
  return (
    <LegalShell title="Terms of Service">
      <p>By using Syllabify you agree to these terms.</p>
      <H>The service</H>
      <p>Syllabify extracts information from uploaded syllabi to help you organize your semester. Extraction is automated and may contain errors — you are responsible for reviewing and confirming all dates and details before relying on them.</p>
      <H>Accuracy</H>
      <p>Syllabify never intentionally invents deadlines. When information is unclear it is flagged for your review. Always confirm critical dates against your official course materials.</p>
      <H>Payment</H>
      <p>Syllabify Lifetime Access is a one-time payment of $4.99 USD. It is not a subscription and does not renew. Access is tied to your account.</p>
      <H>Acceptable use</H>
      <p>Do not upload content you do not have the right to use. Do not attempt to disrupt or misuse the service.</p>
      <H>Account termination</H>
      <p>You may delete your account at any time. We may suspend accounts that violate these terms.</p>
      <H>Disclaimer</H>
      <p>Syllabify is provided "as is" without warranties. We are not liable for missed deadlines or academic outcomes.</p>
      <H>Contact</H>
      <p>Questions? Contact support@syllabify.app.</p>
    </LegalShell>
  );
}
